/**
 * BST Compare Page — cross-tab selection · BRM-style comparison table
 */
(function () {
  'use strict';

  const ACTIVE = 'is-active';
  const COMPARING = 'is-comparing';
  const DESC_TRUNCATE = 200;

  const COMPARE_CHECKMARK_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="12" viewBox="0 0 15 12" fill="none" aria-hidden="true" focusable="false"><path d="M-0.000355005 6.04368L2.00249 4.03374L5.29084 7.26527L12.5849 -0.000355005L14.6019 2.00959L5.29084 11.271L-0.000355005 6.04368Z" fill="#9D1D22"/></svg>`;
  const COMPARE_PRODUCT_LIMIT = 3;

  const SELECTORS = {
    root: '[data-bst-compare-page]',
    tab: '[data-bst-compare-tab]',
    panel: '[data-bst-compare-panel]',
    compareSubmit: '[data-bst-compare-submit]',
    reset: '[data-bst-compare-reset]',
    select: '[data-bst-compare-select]',
    productCell: '[data-bst-compare-product-cell]',
    heroHeading: '[data-bst-compare-hero-heading]',
    heroSubheading: '[data-bst-compare-hero-subheading]',
    selection: '[data-bst-compare-selection]',
    selectedRow: '[data-bst-compare-selected-row]',
    comparisonView: '[data-bst-compare-comparison-view]',
    tableMount: '[data-bst-compare-table-mount]',
    pickerTrack: '[data-bst-compare-picker-track]',
    pickerDots: '[data-bst-compare-picker-dots]',
    schemaScript: '[data-bst-compare-schema]',
    allProductsScript: '[data-bst-compare-all-products]',
    cardTemplate: '[data-bst-compare-card-template]',
    resultTemplate: '[data-bst-compare-result-template]',
    mobileCardsTrack: '[data-bst-compare-mobile-cards-track]',
    mobileCardsDots: '[data-bst-compare-mobile-cards-dots]',
    tableWrap: '[data-bst-compare-table-wrap]',
  };

  function parseJsonScript(node) {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('BST Compare: invalid JSON', error);
      return null;
    }
  }

  async function loadSchema(assetUrl) {
    if (!assetUrl) return { groups: [], use_product_specs: true };

    try {
      const response = await fetch(assetUrl);
      if (!response.ok) throw new Error('Schema fetch failed');
      return await response.json();
    } catch (error) {
      console.warn('BST Compare: schema load failed', error);
      return { groups: [], use_product_specs: true };
    }
  }

  const MOBILE_CARD_WIDTH = 218;
  const MOBILE_CARD_GAP = 3;
  const MOBILE_COMPARE_MAX = 989;

  function getMobileScrollStep(root) {
    const cardWidth =
      parseFloat(getComputedStyle(root).getPropertyValue('--bst-compare-mobile-card-width')) || MOBILE_CARD_WIDTH;
    const gap =
      parseFloat(getComputedStyle(root).getPropertyValue('--bst-compare-mobile-card-gap')) || MOBILE_CARD_GAP;
    return cardWidth + gap;
  }

  function getMobileColumnWidth(root) {
    return parseFloat(getComputedStyle(root).getPropertyValue('--product-compare-column-width')) || MOBILE_CARD_WIDTH;
  }

  function getMobileMaxScroll(element) {
    if (!element) return 0;
    return Math.max(0, element.scrollWidth - element.clientWidth);
  }

  function getMobileCardTargetScroll(root, track, index, count) {
    const step = getMobileScrollStep(root);
    if (index >= count - 1) {
      return getMobileMaxScroll(track);
    }
    return index * step;
  }

  function getMobileTableTargetScroll(root, tableWrap, index, count) {
    const columnWidth = getMobileColumnWidth(root);
    if (index >= count - 1) {
      return getMobileMaxScroll(tableWrap);
    }
    return index * columnWidth;
  }

  function isMobileCompareView() {
    return window.matchMedia(`(max-width: ${MOBILE_COMPARE_MAX}px)`).matches;
  }

  function getLabels(root) {
    return {
      compare: root.dataset.compareLabel || 'Compare',
      reset: root.dataset.resetLabel || 'Reset comparison',
      seeMore: root.dataset.seeMoreLabel || 'See more',
      shopNow: root.dataset.shopNowLabel || 'Shop Now',
      comparing: root.dataset.comparingLabel || 'Comparing',
      legendBestValue: root.dataset.legendBestValue || 'Best value',
      legendExclusive: root.dataset.legendExclusive || 'Exclusive feature',
      legendNotIncluded: root.dataset.legendNotIncluded || 'Not included',
      selectProduct: 'Select a product',
    };
  }

  function normalizeText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function containsHtml(text) {
    return /<[a-z][\s\S]*?>/i.test(String(text || ''));
  }

  function normalizeBrTags(text) {
    return String(text || '').replace(/<br\s*\/?>/gi, '<br>');
  }

  function sanitizeCompareHtml(html) {
    const source = normalizeBrTags(html);
    if (!source) return '';

    const doc = new DOMParser().parseFromString(source, 'text/html');
    const allowedTags = new Set(['BR', 'A', 'P', 'UL', 'OL', 'LI', 'STRONG', 'EM', 'B', 'I', 'SPAN', 'DIV']);
    const allowedAttrs = {
      A: new Set(['href', 'target', 'rel', 'class']),
      SPAN: new Set(['class']),
      DIV: new Set(['class']),
      P: new Set(['class']),
    };

    const sanitizeNode = (node) => {
      [...node.childNodes].forEach((child) => {
        if (child.nodeType !== Node.ELEMENT_NODE) return;

        if (!allowedTags.has(child.tagName)) {
          const fragment = document.createDocumentFragment();
          while (child.firstChild) fragment.appendChild(child.firstChild);
          child.replaceWith(fragment);
          sanitizeNode(node);
          return;
        }

        [...child.attributes].forEach((attr) => {
          const tagAttrs = allowedAttrs[child.tagName];
          if (!tagAttrs || !tagAttrs.has(attr.name.toLowerCase())) {
            child.removeAttribute(attr.name);
          }
        });

        if (child.tagName === 'A') {
          const href = child.getAttribute('href') || '';
          if (!/^https?:\/\//i.test(href) && !href.startsWith('/') && !href.startsWith('#')) {
            child.removeAttribute('href');
          } else {
            child.setAttribute('rel', 'noopener noreferrer');
          }
        }

        sanitizeNode(child);
      });
    };

    sanitizeNode(doc.body);
    return doc.body.innerHTML.trim();
  }

  function renderRichTextValue(text) {
    const value = String(text || '').trim();
    if (!value || value === '—') {
      return '<p class="product-compare__value product-compare__value--muted">—</p>';
    }

    if (containsHtml(value)) {
      return `<div class="product-compare__value product-compare__value--html rte">${sanitizeCompareHtml(value)}</div>`;
    }

    return `<p class="product-compare__value">${escapeHtml(value)}</p>`;
  }

  function renderLabelText(text) {
    const value = String(text || '').trim();
    if (!value) return '';

    if (containsHtml(value)) {
      return sanitizeCompareHtml(value);
    }

    return escapeHtml(value);
  }

  function formatHeroHeading(heading, highlight) {
    const text = String(heading || '');
    const word = String(highlight || '').trim();
    if (!text || !word || !text.includes(word)) return escapeHtml(text);

    return text
      .split(word)
      .map((part, index, parts) => {
        const chunk = escapeHtml(part);
        if (index === parts.length - 1) return chunk;
        return `${chunk}<span class="bst-compare-page__hero-heading-highlight">${escapeHtml(word)}</span>`;
      })
      .join('');
  }

  function truncateText(text, limit) {
    const value = String(text || '').trim();
    if (value.length <= limit) return { short: value, truncated: false };
    const cut = value.slice(0, limit).replace(/\s+\S*$/, '');
    return { short: cut.trim(), truncated: true };
  }

  function renderCompareAddToCartButton(product, labels) {
    const label = product.available === false ? 'Sold out' : product.shopLabel || labels.shopNow;
    const disabledAttrs = product.available === false ? ' disabled aria-disabled="true"' : '';

    return `
      <product-form class="bst-compare-product-cell__form" data-hide-errors="true">
        <form method="post" action="/cart/add" class="form" novalidate="novalidate" data-type="add-to-cart-form">
          <input type="hidden" name="id" value="${escapeHtml(String(product.variantId || ''))}">
          <button type="submit" name="add" class="bst-compare-product-cell__cta"${disabledAttrs}>
            <span class="bst-compare-product-cell__cta-label">${escapeHtml(label)}</span>
            <div class="loading__spinner hidden">
              <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66" aria-hidden="true" focusable="false"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/></svg>
            </div>
          </button>
        </form>
      </product-form>
    `;
  }

  function renderAttributeLabel(row) {
    const name = `<span class="product-compare__attribute-name">${renderLabelText(row.label)}</span>`;
    const description = row.description
      ? `<span class="product-compare__attribute-description">${renderLabelText(row.description)}</span>`
      : '';
    const detailClass = row.is_detail_block ? ' product-compare__attribute-label--detail' : '';

    return `<th scope="row" class="product-compare__attribute-label${detailClass}">${name}${description}</th>`;
  }

  function resolveRowValue(product, row) {
    if (row.auto_price) return { type: 'text', value: product.price || '—' };
    if (row.auto_description) {
      if (isMobileCompareView()) {
        const cardText = product.tagline || product.excerpt || '—';
        return { type: 'card-text', value: cardText };
      }
      return { type: 'description', value: product.excerpt || product.description || '—' };
    }
    if (row.auto_brand) return { type: 'text', value: product.vendor || '—' };
    if (row.auto_availability) return { type: 'text', value: product.availability || '—' };
    if (row.auto_series) return { type: 'text', value: product.series || product.subtitle || '—' };

    if (row.detail_block_id) {
      const block = (product.detailBlocks || []).find((item) => item.id === row.detail_block_id);
      if (block?.contentHtml || block?.content) {
        return {
          type: 'detail-block',
          headline: block.headline || row.label || '',
          html: block.contentHtml || '',
          plain: block.content || '',
        };
      }
      return { type: 'text', value: '—' };
    }

    const rows = product.rows || {};
    if (rows[row.id]) return rows[row.id];

    const labelKey = normalizeText(row.label);
    const specMatch = (product.specifications || []).find((spec) => normalizeText(spec.label) === labelKey);
    if (specMatch) return { type: 'text', value: specMatch.value || '—' };

    return { type: 'text', value: '—' };
  }

  function renderCellContent(value, labels) {
    const type = value?.type || 'text';

    if (type === 'detail-block') {
      const bodyHtml =
        value.html ||
        (value.plain ? `<p>${escapeHtml(value.plain)}</p>` : '');

      if (!bodyHtml) {
        return '<p class="product-compare__value product-compare__value--muted">—</p>';
      }

      return `
        <div class="bst-product-details__block bst-product-details__block--compare">
          <div class="bst-product-details__body rte product-compare__value product-compare__value--html">
            ${bodyHtml}
          </div>
        </div>
      `;
    }

    if (type === 'card-text') {
      const text = value.value || '—';
      if (!text || text === '—') {
        return '<p class="product-compare__value product-compare__value--muted">—</p>';
      }

      return `<p class="product-compare__value product-compare__value--card">${escapeHtml(text)}</p>`;
    }

    if (type === 'description') {
      const text = value.value || '—';
      const { short, truncated } = truncateText(text, DESC_TRUNCATE);
      const moreButton = truncated
        ? `<button type="button" class="product-compare__see-more" data-bst-compare-see-more>${escapeHtml(labels.seeMore)}</button>`
        : '';
      return `
        <div class="product-compare__description" data-bst-compare-description>
          <p class="product-compare__value" data-bst-compare-description-short>${escapeHtml(short)}${truncated ? '…' : ''}</p>
          <p class="product-compare__value product-compare__value--full" data-bst-compare-description-full hidden>${escapeHtml(text)}</p>
          ${moreButton}
        </div>
      `;
    }

    if (type === 'boolean') {
      if (value.included) {
        const detail = value.subvalue || value.value || 'Included';
        return renderRichTextValue(detail);
      }
      return '<p class="product-compare__value product-compare__value--muted">Not included</p>';
    }

    if (type === 'exclusive') {
      return renderRichTextValue(value.label || value.value || 'Exclusive');
    }

    const main = renderRichTextValue(value?.value || '—');
    const sub = value?.subvalue
      ? `<div class="product-compare__subvalue">${containsHtml(value.subvalue) ? sanitizeCompareHtml(value.subvalue) : escapeHtml(value.subvalue)}</div>`
      : '';
    return `${main}${sub}`;
  }

  function buildComparisonGroups(products, schema) {
    const groups = JSON.parse(JSON.stringify(schema.groups || []));

    const detailsGroupIndex = groups.findIndex((group) => group.use_detail_blocks);
    if (detailsGroupIndex >= 0) {
      const headlineMap = new Map();
      products.forEach((product) => {
        (product.detailBlocks || []).forEach((block) => {
          const key = block.id || normalizeText(block.headline);
          if (!key || headlineMap.has(key)) return;
          headlineMap.set(key, {
            id: key,
            label: block.headline || key,
            detail_block_id: key,
            is_detail_block: true,
          });
        });
      });
      groups[detailsGroupIndex] = {
        ...groups[detailsGroupIndex],
        rows: Array.from(headlineMap.values()),
      };
    }

    if (schema.use_product_specs) {
      const labelMap = new Map();
      products.forEach((product) => {
        (product.specifications || []).forEach((spec) => {
          const key = normalizeText(spec.label);
          if (!key || labelMap.has(key)) return;
          labelMap.set(key, {
            id: `spec-${key.replace(/\s+/g, '-')}`,
            label: spec.label,
          });
        });
      });

      const specGroupIndex = groups.findIndex((group) => group.id === 'specifications');
      const specRows = Array.from(labelMap.values());
      if (specGroupIndex >= 0) {
        groups[specGroupIndex] = { ...groups[specGroupIndex], rows: specRows };
      } else if (specRows.length) {
        groups.push({ id: 'specifications', title: 'Specifications', rows: specRows });
      }
    }

    return groups.filter((group) => (group.rows || []).length > 0);
  }

  class ComparePage {
    constructor(root) {
      this.root = root;
      this.labels = getLabels(root);
      this.panels = [...root.querySelectorAll(SELECTORS.panel)];
      this.tabs = [...root.querySelectorAll(SELECTORS.tab)];
      this.panelByTab = new Map();
      this.schemas = new Map();
      this.productCatalog = new Map();
      this.selectedIds = [];
      this.comparing = false;
      this.activeTab = null;
      this.mobileActiveIndex = -1;
      this._mobileScrollLock = null;
      this._mobileScrollLockTimer = null;
      this.minProducts = COMPARE_PRODUCT_LIMIT;
      this.maxProducts = COMPARE_PRODUCT_LIMIT;
      this.visibleCompareColumns = Number(root.dataset.visibleCompareColumns) || 3;
    }

    async init() {
      if (!this.panels.length) return;

      this.buildProductCatalog();
      this.panels.forEach((panel) => {
        const tabKey = panel.dataset.bstComparePanel;
        if (tabKey) this.panelByTab.set(tabKey, panel);
      });

      this.root.style.setProperty('--product-compare-visible-columns', String(this.visibleCompareColumns));
      this.root.style.setProperty('--bst-compare-columns', String(this.maxProducts));

      this.bindPicker();
      this.bindDescriptionToggle();
      this.bindTabs();
      this.bindCompare();
      this.bindReset();
      this.initPickerDots();

      const initialTab =
        this.tabs.find((tab) => tab.classList.contains(ACTIVE))?.dataset.bstCompareTab ||
        this.tabs[0]?.dataset.bstCompareTab;

      await this.activateTab(initialTab, { preserveComparison: false });
    }

    buildProductCatalog() {
      const products = parseJsonScript(this.root.querySelector(SELECTORS.allProductsScript)) || [];
      products.forEach((product) => {
        if (product?.id) this.productCatalog.set(product.id, product);
      });

      this.panels.forEach((panel) => {
        const panelProducts = parseJsonScript(panel.querySelector('[data-bst-compare-products]')) || [];
        panelProducts.forEach((product) => {
          if (product?.id) this.productCatalog.set(product.id, product);
        });
      });
    }

    async getSchemaForTab(tabKey) {
      if (this.schemas.has(tabKey)) return this.schemas.get(tabKey);

      const panel = this.panelByTab.get(tabKey);
      const script = panel?.querySelector(SELECTORS.schemaScript);
      const assetUrl = parseJsonScript(script);
      const schema = await loadSchema(assetUrl);
      this.schemas.set(tabKey, schema);
      return schema;
    }

    getSelectedProducts() {
      return this.selectedIds.map((id) => this.productCatalog.get(id)).filter(Boolean);
    }

    countSelected() {
      return this.selectedIds.length;
    }

    canCompare() {
      return this.countSelected() >= this.minProducts && this.countSelected() <= this.maxProducts;
    }

    toggleProduct(productId) {
      if (this.comparing) return;

      const index = this.selectedIds.indexOf(productId);
      if (index >= 0) {
        this.selectedIds.splice(index, 1);
      } else if (this.selectedIds.length < this.maxProducts) {
        this.selectedIds.push(productId);
      } else {
        this.selectedIds[this.maxProducts - 1] = productId;
      }

      this.syncAllPickers();
      this.renderSelectedRow();
      this.updateNavActions();
    }

    enterComparison() {
      if (!this.canCompare()) return;

      this.comparing = true;
      this.root.classList.add(COMPARING);

      const selection = this.root.querySelector(SELECTORS.selection);
      const comparisonView = this.root.querySelector(SELECTORS.comparisonView);
      if (selection) selection.hidden = true;
      if (comparisonView) comparisonView.hidden = false;

      this.renderComparison();
      this.updateNavActions();

      comparisonView?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    exitComparisonView() {
      this.comparing = false;
      this.root.classList.remove(COMPARING);

      const selection = this.root.querySelector(SELECTORS.selection);
      const comparisonView = this.root.querySelector(SELECTORS.comparisonView);
      if (selection) selection.hidden = false;
      if (comparisonView) comparisonView.hidden = true;
    }

    resetComparison() {
      this.selectedIds = [];
      this.exitComparisonView();
      this.syncAllPickers();
      this.renderSelectedRow();
      this.updateNavActions();
    }

    syncAllPickers() {
      this.panels.forEach((panel) => {
        panel.querySelectorAll(SELECTORS.productCell).forEach((cell) => {
          const id = Number(cell.dataset.productId);
          const selected = this.selectedIds.includes(id);
          cell.classList.toggle('is-selected', selected);
          cell.classList.toggle('is-dimmed', this.selectedIds.length >= this.maxProducts && !selected);
          const button = cell.querySelector(SELECTORS.select);
          if (button) button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
      });
    }

    bindPicker() {
      this.root.addEventListener('click', (event) => {
        const button = event.target.closest(SELECTORS.select);
        if (!button) return;

        event.preventDefault();
        event.stopPropagation();

        const cell = button.closest(SELECTORS.productCell);
        if (!cell) return;

        this.toggleProduct(Number(cell.dataset.productId));
      });
    }

    bindDescriptionToggle() {
      this.root.addEventListener('click', (event) => {
        const button = event.target.closest('[data-bst-compare-see-more]');
        if (!button) return;

        const wrapper = button.closest('[data-bst-compare-description]');
        if (!wrapper) return;

        wrapper.querySelector('[data-bst-compare-description-short]')?.setAttribute('hidden', '');
        wrapper.querySelector('[data-bst-compare-description-full]')?.removeAttribute('hidden');
        button.remove();
      });
    }

    updateNavActions() {
      const compareButtons = [...this.root.querySelectorAll(SELECTORS.compareSubmit)];
      const resetButtons = [...this.root.querySelectorAll(SELECTORS.reset)];
      if (!compareButtons.length || !resetButtons.length) return;

      compareButtons.forEach((button) => {
        button.hidden = this.comparing || !this.canCompare();
      });

      resetButtons.forEach((button) => {
        button.hidden = !this.comparing;
      });
    }

    renderSelectedRow() {
      const row = this.root.querySelector(SELECTORS.selectedRow);
      if (!row) return;

      const slots = Array.from({ length: this.maxProducts }, (_, index) => this.selectedIds[index] || null);

      row.innerHTML = slots
        .map((productId) => {
          if (!productId) {
            return `
              <div class="bst-compare-page__selected-slot bst-compare-page__selected-slot--empty">
                <span class="bst-compare-page__selected-slot-label">${escapeHtml(this.labels.selectProduct)}</span>
              </div>
            `;
          }

          const product = this.productCatalog.get(productId);
          if (!product) return '';

          const imageHtml = product.image
            ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" class="bst-compare-page__selected-thumb" width="48" height="48" loading="lazy">`
            : '<span class="bst-compare-page__selected-thumb bst-compare-page__selected-thumb--fallback" aria-hidden="true"></span>';

          return `
            <div class="bst-compare-page__selected-slot">
              <div class="bst-compare-page__selected-item" aria-label="${escapeHtml(product.title)}">
                ${imageHtml}
                <p class="bst-compare-page__selected-title">${escapeHtml(product.title)}</p>
              </div>
            </div>
          `;
        })
        .join('');
    }

    renderCornerCell() {
      return `
        <td class="product-compare__corner-cell">
          <div class="product-compare__corner-inner">
            <p class="product-compare__corner-label">${escapeHtml(this.labels.comparing)}</p>
            <ul class="product-compare__legend-list" role="list">
              <li>
                <span class="product-compare__legend-icon product-compare__legend-icon--yes" aria-hidden="true">${COMPARE_CHECKMARK_SVG}</span>
                <span>${escapeHtml(this.labels.legendBestValue)}</span>
              </li>
              <li class="product-compare__legend-item--exclusive">
                <span class="product-compare__legend-icon product-compare__legend-icon--exclusive" aria-hidden="true">★</span>
                <span>${escapeHtml(this.labels.legendExclusive)}</span>
              </li>
              <li>
                <span class="product-compare__legend-icon product-compare__legend-icon--no" aria-hidden="true"></span>
                <span>${escapeHtml(this.labels.legendNotIncluded)}</span>
              </li>
            </ul>
          </div>
        </td>
      `;
    }

    renderProductHeaderCell(productId) {
      const template = this.root.querySelector(`${SELECTORS.cardTemplate}[data-product-id="${productId}"]`);
      if (template) {
        return `
          <th scope="col" class="product-compare__product-cell">
            <div class="product-compare__product-card">${template.innerHTML}</div>
          </th>
        `;
      }

      const product = this.productCatalog.get(productId);
      if (!product) return '';

      return `
        <th scope="col" class="product-compare__product-cell">
          <div class="product-compare__product-card">
            <a href="${escapeHtml(product.url)}" class="product-compare__product-title">${escapeHtml(product.title)}</a>
            <p class="product-compare__product-price">${escapeHtml(product.price || '')}</p>
          </div>
        </th>
      `;
    }

    async renderComparison() {
      const products = this.getSelectedProducts();
      const schema = await this.getSchemaForTab(this.activeTab);
      const groups = buildComparisonGroups(products, schema || { groups: [] });
      this.renderProductCompareTable(products, groups);
    }

    renderProductCompareTable(products, groups) {
      const mount = this.root.querySelector(SELECTORS.tableMount);
      if (!mount) return;

      const productHeaders = products.map((product) => this.renderProductHeaderCell(product.id)).join('');
      const columnCount = products.length + 1;

      const bodyRows = groups
        .map((group) => {
          const attributeRows = (group.rows || [])
            .map((row) => {
              const cells = products
                .map((product, columnIndex) => {
                  const value = resolveRowValue(product, row);
                  return `<td class="product-compare__value-cell" data-compare-column="${columnIndex}">${renderCellContent(value, this.labels)}</td>`;
                })
                .join('');

              const labelMarkup = renderAttributeLabel(row);

              return `
                <tr class="product-compare__attribute-row${row.is_detail_block ? ' product-compare__attribute-row--detail-block' : ''}">
                  ${labelMarkup}
                  ${cells}
                </tr>
              `;
            })
            .join('');

          if (!attributeRows) return '';

          return `
            <tr class="product-compare__group-row">
              <th scope="colgroup" colspan="${columnCount}">
                <div class="product-compare__group-row-bar">${escapeHtml(group.title)}</div>
              </th>
            </tr>
            ${attributeRows}
          `;
        })
        .join('');

      mount.innerHTML = `
        <table class="product-compare__table">
          <caption class="visually-hidden">Product comparison</caption>
          <thead class="product-compare__desktop-head">
            <tr class="product-compare__product-row">
              ${this.renderCornerCell()}
              ${productHeaders}
            </tr>
          </thead>
          <tbody>
            ${bodyRows || `<tr><td colspan="${columnCount}"><p class="product-compare__empty">No comparison data available.</p></td></tr>`}
          </tbody>
        </table>
      `;

      this.renderMobileComparisonCards(products);
      this.bindGroupRowLayout();
      this.commitMobileSlide(0);
    }

    lockMobileScroll(source) {
      this._mobileScrollLock = source;
      if (this._mobileScrollLockTimer) {
        clearTimeout(this._mobileScrollLockTimer);
      }
      this._mobileScrollLockTimer = window.setTimeout(() => {
        this._mobileScrollLock = null;
      }, 200);
    }

    commitMobileSlide(index, options = {}) {
      if (!isMobileCompareView()) return;

      const { fromUser = false, origin = 'both' } = options;
      const track = this.root.querySelector(SELECTORS.mobileCardsTrack);
      const tableWrap = this.root.querySelector(SELECTORS.tableWrap);
      const dotsWrap = this.root.querySelector(SELECTORS.mobileCardsDots);
      const cards = track ? [...track.querySelectorAll('.product-compare__mobile-card')] : [];
      const count = cards.length;

      if (!count) return;

      const nextIndex = Math.max(0, Math.min(index, count - 1));
      const cardTargetLeft = getMobileCardTargetScroll(this.root, track, nextIndex, count);
      const tableTargetLeft = tableWrap
        ? getMobileTableTargetScroll(this.root, tableWrap, nextIndex, count)
        : 0;

      if (nextIndex === this.mobileActiveIndex && !fromUser) {
        if (origin !== 'table' && tableWrap) tableWrap.scrollLeft = tableTargetLeft;
        return;
      }

      this.mobileActiveIndex = nextIndex;

      if (dotsWrap) {
        dotsWrap.querySelectorAll('.product-compare__mobile-dot').forEach((dot, dotIndex) => {
          dot.classList.toggle(ACTIVE, dotIndex === nextIndex);
        });
      }

      if (origin !== 'cards' && track && Math.abs(track.scrollLeft - cardTargetLeft) > 1) {
        this.lockMobileScroll(origin === 'table' ? 'table' : 'both');
        track.scrollTo({ left: cardTargetLeft, behavior: fromUser ? 'smooth' : 'auto' });
      }

      if (origin !== 'table' && tableWrap) {
        this.lockMobileScroll(origin === 'cards' ? 'cards' : 'both');
        tableWrap.scrollLeft = tableTargetLeft;
      }
    }

    syncMobileSlide(index, options = {}) {
      this.commitMobileSlide(index, options);
    }

    getMobileCardIndex(track) {
      const cards = [...track.querySelectorAll('.product-compare__mobile-card')];
      if (!cards.length) return 0;

      const maxScroll = getMobileMaxScroll(track);
      if (track.scrollLeft >= maxScroll - 2) {
        return cards.length - 1;
      }

      const step = getMobileScrollStep(this.root);
      const index = Math.round(track.scrollLeft / step);
      return Math.max(0, Math.min(cards.length - 1, index));
    }

    getMobileTableIndex(tableWrap, count) {
      if (!tableWrap || !count) return 0;

      const maxScroll = getMobileMaxScroll(tableWrap);
      if (tableWrap.scrollLeft >= maxScroll - 2) {
        return count - 1;
      }

      const columnWidth = getMobileColumnWidth(this.root);
      const index = Math.round(tableWrap.scrollLeft / columnWidth);
      return Math.max(0, Math.min(count - 1, index));
    }

    syncGroupRowWidths() {
      const tableWrap = this.root.querySelector(SELECTORS.tableWrap);
      if (!tableWrap) return;

      const width = tableWrap.clientWidth;
      const nextWidth = `${width}px`;
      const currentWidth = this.root.style.getPropertyValue('--product-compare-viewport-width');

      if (currentWidth === nextWidth) return;

      this.root.style.setProperty('--product-compare-viewport-width', nextWidth);
    }

    bindGroupRowLayout() {
      const tableWrap = this.root.querySelector(SELECTORS.tableWrap);
      if (!tableWrap) return;

      this.syncGroupRowWidths();
      requestAnimationFrame(() => this.syncGroupRowWidths());

      if (tableWrap._bstCompareResizeObserver) {
        tableWrap._bstCompareResizeObserver.disconnect();
      }

      if (typeof ResizeObserver !== 'undefined') {
        let resizeFrame = 0;
        tableWrap._bstCompareResizeObserver = new ResizeObserver(() => {
          if (resizeFrame) return;
          resizeFrame = requestAnimationFrame(() => {
            resizeFrame = 0;
            this.syncGroupRowWidths();
          });
        });
        tableWrap._bstCompareResizeObserver.observe(tableWrap);
        return;
      }

      if (tableWrap._bstCompareResizeHandler) {
        window.removeEventListener('resize', tableWrap._bstCompareResizeHandler);
      }

      tableWrap._bstCompareResizeHandler = () => this.syncGroupRowWidths();
      window.addEventListener('resize', tableWrap._bstCompareResizeHandler);
    }

    renderMobileComparisonCards(products) {
      const track = this.root.querySelector(SELECTORS.mobileCardsTrack);
      const dotsWrap = this.root.querySelector(SELECTORS.mobileCardsDots);
      if (!track) return;

      track.innerHTML = products
        .map((product) => {
          const template = this.root.querySelector(
            `${SELECTORS.resultTemplate}[data-product-id="${product.id}"]`
          );
          const cardHtml = template
            ? template.innerHTML
            : this.renderMobileResultCardFallback(product);

          return `<div class="product-compare__mobile-card" data-product-id="${product.id}">${cardHtml}</div>`;
        })
        .join('');

      if (!dotsWrap) return;

      if (products.length <= 1) {
        dotsWrap.innerHTML = '';
        dotsWrap.hidden = true;
        this.commitMobileSlide(0);
        return;
      }

      dotsWrap.hidden = false;
      dotsWrap.innerHTML = products
        .map(
          (_, index) =>
            `<button type="button" class="product-compare__mobile-dot${index === 0 ? ` ${ACTIVE}` : ''}" data-compare-dot="${index}" aria-label="Show product ${index + 1}"></button>`
        )
        .join('');

      this.bindMobileCardsScroll(track, dotsWrap);
    }

    renderMobileResultCardFallback(product) {
      const imageHtml = product.image
        ? `<img src="${escapeHtml(product.image)}" alt="" class="bst-compare-product-cell__image" width="218" height="140" loading="lazy">`
        : '';

      const categoryHtml = product.category
        ? `<p class="bst-compare-product-cell__category bst-cat-sm">${escapeHtml(product.category)}</p>`
        : '';

      const reviewHtml =
        product.reviewCount || product.rating
          ? `<p class="bst-compare-product-cell__rating">
              <span class="bst-compare-product-cell__stars" aria-hidden="true">★★★★★</span>
              <span class="bst-compare-product-cell__review-count">${escapeHtml(String(product.reviewCount || ''))} reviews</span>
            </p>`
          : '';

      return `
        <article class="bst-compare-product-cell bst-compare-product-cell--result">
          <div class="bst-compare-product-cell__media">${imageHtml}</div>
          <div class="bst-compare-product-cell__body">
            ${categoryHtml}
            <h3 class="bst-compare-product-cell__title bst-product-name">${escapeHtml(product.title)}</h3>
            <div class="bst-compare-product-cell__meta">
              <p class="bst-compare-product-cell__price bst-cat-md">${escapeHtml(product.price || '')}</p>
              ${reviewHtml}
            </div>
            ${renderCompareAddToCartButton(product, this.labels)}
          </div>
        </article>
      `;
    }

    bindMobileCardsScroll(track, dotsWrap) {
      const tableWrap = this.root.querySelector(SELECTORS.tableWrap);
      const cardCount = track.querySelectorAll('.product-compare__mobile-card').length;

      if (track._bstCompareScrollHandler) {
        track.removeEventListener('scroll', track._bstCompareScrollHandler);
      }

      if (track._bstCompareScrollEndTimer) {
        clearTimeout(track._bstCompareScrollEndTimer);
      }

      if (typeof track._bstCompareScrollEndHandler === 'function') {
        track.removeEventListener('scrollend', track._bstCompareScrollEndHandler);
      }

      if (tableWrap?._bstCompareTableScrollHandler) {
        tableWrap.removeEventListener('scroll', tableWrap._bstCompareTableScrollHandler);
      }

      if (tableWrap?._bstCompareTableScrollEndTimer) {
        clearTimeout(tableWrap._bstCompareTableScrollEndTimer);
      }

      if (typeof tableWrap?._bstCompareTableScrollEndHandler === 'function') {
        tableWrap.removeEventListener('scrollend', tableWrap._bstCompareTableScrollEndHandler);
      }

      const settleFromCards = () => {
        if (this._mobileScrollLock === 'table' || this._mobileScrollLock === 'both') return;
        const index = this.getMobileCardIndex(track);
        this.commitMobileSlide(index, { origin: 'cards' });
      };

      track._bstCompareScrollEndHandler = settleFromCards;
      if ('onscrollend' in window) {
        track.addEventListener('scrollend', track._bstCompareScrollEndHandler);
      }

      track._bstCompareScrollHandler = () => {
        if (track._bstCompareScrollEndTimer) {
          clearTimeout(track._bstCompareScrollEndTimer);
        }

        track._bstCompareScrollEndTimer = window.setTimeout(settleFromCards, 120);
      };

      track.addEventListener('scroll', track._bstCompareScrollHandler, { passive: true });

      if (tableWrap) {
        const settleFromTable = () => {
          if (this._mobileScrollLock === 'cards' || this._mobileScrollLock === 'both') return;
          const index = this.getMobileTableIndex(tableWrap, cardCount);
          this.commitMobileSlide(index, { origin: 'table' });
        };

        tableWrap._bstCompareTableScrollEndHandler = settleFromTable;
        if ('onscrollend' in window) {
          tableWrap.addEventListener('scrollend', tableWrap._bstCompareTableScrollEndHandler);
        }

        tableWrap._bstCompareTableScrollHandler = () => {
          if (tableWrap._bstCompareTableScrollEndTimer) {
            clearTimeout(tableWrap._bstCompareTableScrollEndTimer);
          }

          tableWrap._bstCompareTableScrollEndTimer = window.setTimeout(settleFromTable, 120);
        };

        tableWrap.addEventListener('scroll', tableWrap._bstCompareTableScrollHandler, { passive: true });
      }

      dotsWrap.querySelectorAll('[data-compare-dot]').forEach((dot) => {
        if (dot._bstCompareDotHandler) {
          dot.removeEventListener('click', dot._bstCompareDotHandler);
        }

        dot._bstCompareDotHandler = () => {
          const index = Number(dot.dataset.compareDot);
          this.commitMobileSlide(index, { fromUser: true });
        };

        dot.addEventListener('click', dot._bstCompareDotHandler);
      });

      this.commitMobileSlide(0);
    }

    async activateTab(tabKey, options = {}) {
      const { preserveComparison = true } = options;
      if (!tabKey) return;

      if (!preserveComparison && this.comparing) {
        this.exitComparisonView();
      }

      this.activeTab = tabKey;

      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.bstCompareTab === tabKey;
        tab.classList.toggle(ACTIVE, isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      let activePanel = null;
      for (const panel of this.panels) {
        const isActive = panel.dataset.bstComparePanel === tabKey;
        panel.classList.toggle(ACTIVE, isActive);
        panel.toggleAttribute('hidden', !isActive);
        if (isActive) activePanel = panel;
      }

      if (activePanel) this.updateHero(activePanel);

      await this.getSchemaForTab(tabKey);
      this.syncAllPickers();
      this.renderSelectedRow();
      this.updateNavActions();

      if (this.comparing) {
        await this.renderComparison();
      }
    }

    updateHero(panel) {
      const heading = this.root.querySelector(SELECTORS.heroHeading);
      const subheading = this.root.querySelector(SELECTORS.heroSubheading);
      const panelHeading = panel.dataset.panelHeading;
      const panelSubheading = panel.dataset.panelSubheading;
      const panelHighlight =
        panel.dataset.panelHeadingHighlight || this.root.dataset.defaultHeadingHighlight || '';

      if (heading && panelHeading) {
        heading.innerHTML = formatHeroHeading(panelHeading, panelHighlight);
      }
      if (subheading && panelSubheading) subheading.textContent = panelSubheading;
    }

    bindTabs() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activateTab(tab.dataset.bstCompareTab));
      });
    }

    bindCompare() {
      this.root.querySelectorAll(SELECTORS.compareSubmit).forEach((button) => {
        button.addEventListener('click', () => this.enterComparison());
      });
    }

    bindReset() {
      this.root.querySelectorAll(SELECTORS.reset).forEach((button) => {
        button.addEventListener('click', () => this.resetComparison());
      });
    }

    initPickerDots() {
      this.panels.forEach((panel) => {
        const track = panel.querySelector(SELECTORS.pickerTrack);
        const dotsWrap = panel.querySelector(SELECTORS.pickerDots);
        if (!track || !dotsWrap) return;

        const cells = [...track.querySelectorAll(SELECTORS.productCell)];
        if (cells.length <= 1) return;

        dotsWrap.innerHTML = cells
          .map((_, index) => `<span class="bst-compare-page__picker-dot${index === 0 ? ` ${ACTIVE}` : ''}"></span>`)
          .join('');

        const dots = [...dotsWrap.querySelectorAll('.bst-compare-page__picker-dot')];
        track.addEventListener(
          'scroll',
          () => {
            const index = Math.round(track.scrollLeft / (cells[0].offsetWidth + 3));
            dots.forEach((dot, dotIndex) => dot.classList.toggle(ACTIVE, dotIndex === index));
          },
          { passive: true }
        );
      });
    }
  }

  function initRoot(root) {
    if (root._bstCompareInit) return;
    root._bstCompareInit = true;
    new ComparePage(root).init();
  }

  function init() {
    document.querySelectorAll(SELECTORS.root).forEach(initRoot);
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll(SELECTORS.root).forEach(initRoot);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
